/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import { Box } from '@mantine/core';

// Components
import { TextInput } from '@components/atoms/TextInput';
import { Checkbox } from '@components/atoms/Checkbox';
import { Radio } from '@components/atoms/Radio';
import { NumericInput } from '@components/atoms/NumericInput';
import { Select } from '@components/atoms/Select';
import { DatePicker } from '@components/atoms/DatePicker';
import { TagsInput } from '@components/atoms/TagsInput';

// Styles
import classes from './Step1.module.css';

// Utils
import { MONTHS_OPTIONS } from '@utils/options';
import { getUnitLabel } from '@utils/units';
import { convertArrayToLabelValue } from '@utils/functions';

// APIs
import { useBrands } from 'shared/api/get-brands';

// Dayjs
import dayjs from 'dayjs';

// I18n
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mantine/hooks';
import { useOffsetTags } from 'shared/api/get-offset-tags';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const Step1 = (props: { form: any }) => {
	/* ******************************** Hooks ******************************* */
	const { data: brands } = useBrands();
	const { data: offsetTags } = useOffsetTags();

	const { t } = useTranslation();

	/* ******************************** Media Query ******************************* */
	const isBigScreen = useMediaQuery('(min-width: 120em)');

	const getForm = () => {
		return (
			<form className={classes.containerForm}>
				{/* Row for Name & Quantity */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<TextInput
							placeholder={t('global.name')}
							visibleFrom="xxl"
							size="md"
							label={t('offsets.form.name')}
							withAsterisk
							fullWidth={true}
							{...props.form.getInputProps('name')}
						/>
						<TextInput
							placeholder={t('global.name')}
							hiddenFrom="xxl"
							size="sm"
							label={t('offsets.form.name')}
							withAsterisk
							fullWidth={true}
							{...props.form.getInputProps('name')}
						/>
					</Box>
					<Box className={classes.containerInput}>
						<NumericInput
							max={15000}
							decimalScale={
								Math.trunc(
									props.form.getInputProps('quantity')
										.value
								) == 0
									? 1
									: 2
							}
							visibleFrom="xxl"
							size="md"
							label={t('global.quantity')}
							withAsterisk
							unit={getUnitLabel('tco2e')}
							defaultValue={0}
							{...props.form.getInputProps('quantity')}
						/>
						<NumericInput
							max={15000}
							decimalScale={
								Math.trunc(
									props.form.getInputProps('quantity')
										.value
								) == 0
									? 1
									: 2
							}
							hiddenFrom="xxl"
							size="sm"
							label={t('global.quantity')}
							withAsterisk
							unit={getUnitLabel('tco2e')}
							defaultValue={0}
							{...props.form.getInputProps('quantity')}
						/>
					</Box>
				</Box>
				{/* Row for Brand & Tags */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<Select
							visibleFrom="xxl"
							size="md"
							label={t('global.brand')}
							withAsterisk
							data={convertArrayToLabelValue(brands)}
							placeholder={t('placeholders.selectBrand')}
							{...props.form.getInputProps('brand')}
						/>

						<Select
							hiddenFrom="xxl"
							size="sm"
							label={t('global.brand')}
							withAsterisk
							data={convertArrayToLabelValue(brands)}
							placeholder={t('placeholders.selectBrand')}
							{...props.form.getInputProps('brand')}
						/>
					</Box>
					<Box className={classes.containerInput}>
						<TagsInput
							placeholder="Tags"
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.tags')}
							withAsterisk
							data={
								offsetTags &&
								Array.isArray(offsetTags) &&
								offsetTags.map((tag: any) => {
									return {
										color: tag.color,
										label: tag.label,
										value: tag.id,
									};
								})
							}
							{...props.form.getInputProps('tags')}
						/>
					</Box>
				</Box>
				{/* Row for dates emissions */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<DatePicker
							clearable
							label={t('offsets.form.emissionStartDate')}
							withAsterisk
							placeholder="DD/MM/YYYY"
							onChange={() => {}}
							minDate={dayjs('01/01/1970')}
							{...props.form.getInputProps('startDate')}
						/>
					</Box>

					<Box className={classes.containerInput}>
						<DatePicker
							clearable
							label={t('offsets.form.emissionEndDate')}
							withAsterisk
							placeholder="DD/MM/YYYY"
							minDate={
								props.form.getInputProps('startDate')
									.value
									? props.form.getInputProps('startDate')
											.value &&
										dayjs(
											props.form.getInputProps(
												'startDate'
											).value
										).toDate()
									: dayjs('01/01/1970')
							}
							onChange={() => {}}
							{...props.form.getInputProps('endDate')}
						/>
					</Box>
				</Box>
				<Box className={classes.rowCheckbox}>
					<Box className={classes.containerInput}>
						<Checkbox
							disabled
							size="sm"
							checked={false}
							label={t('offsets.setReminder')}
							onClick={() => {}}
						/>
					</Box>
				</Box>
				<Box className={classes.rowSecond}>
					<span>{t('offsets.iWillOffsetEvery')} </span>
					<Box
						hiddenFrom="xxl"
						className={classes.containerOffsetInput}
					>
						<NumericInput
							disabled={true}
							size="sm"
							defaultValue={0}
						/>
					</Box>
					<Box
						visibleFrom="xxl"
						className={classes.containerOffsetInput}
					>
						<NumericInput
							disabled={true}
							size="md"
							defaultValue={0}
						/>
					</Box>
					<Select
						visibleFrom="xxl"
						size="md"
						data={MONTHS_OPTIONS}
						placeholder={t('global.month')}
						onChange={() => {}}
						disabled
					/>
					<Select
						hiddenFrom="xxl"
						size="sm"
						data={MONTHS_OPTIONS}
						placeholder={t('global.month')}
						onChange={() => {}}
						disabled
					/>
				</Box>
				<Box className={classes.rowSecond}>
					<span>{t('offsets.ends')}</span>
					<Radio
						size="sm"
						checked
						label={t('global.ever')}
						onClick={() => {}}
						disabled
					/>
					<Radio
						size="sm"
						checked={false}
						label={t('global.at')}
						onClick={() => {}}
						disabled
					/>
					<Box className={classes.containerDateEndInput}>
						<DatePicker
							visibleFrom="xxl"
							size="lg"
							withAsterisk
							placeholder={t(
								'offsets.placeholders.selectDate'
							)}
							onChange={() => {}}
							disabled
						/>
						<DatePicker
							hiddenFrom="xxl"
							size="md"
							withAsterisk
							placeholder={t(
								'offsets.placeholders.selectDate'
							)}
							onChange={() => {}}
							disabled
						/>
					</Box>
					<Radio
						size="sm"
						checked={false}
						label={t('global.after')}
						onClick={() => {}}
						disabled
					/>
					<Box
						visibleFrom="xxl"
						className={classes.containerOffsetInput}
					>
						<NumericInput
							disabled
							size="md"
							defaultValue={0}
						/>
					</Box>
					<Box
						hiddenFrom="xxl"
						className={classes.containerOffsetInput}
					>
						<NumericInput
							disabled
							size="sm"
							defaultValue={0}
						/>
					</Box>
					<span>{t('global.occurences')}</span>
				</Box>
			</form>
		);
	};

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.container}>
			<Box className={classes.containerEmpty} />
			{getForm()}
			<Box className={classes.containerEmpty} />
		</Box>
	);
};
